// @ts-strict-ignore
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MultiSelect, TextField } from '@usealloy/component-library';
import type { Option, SelectedOption } from '@usealloy/component-library';
import { MULTISELECT_PROPS } from 'flow/editor/components/constants';
import useWorkflowVersionSchemaFields from '~/dashboard/views/workflows/api/useWorkflowVersionSchemaFields';
import type { SchemaField } from '~/helpers/api-new/modules/workflows/workflowsApi';

// This level's own segment (name + array index) of a saved path, plus the raw remainder to
// fall back to whole when `name` doesn't match a known field (a free-typed value can have dots).
function getSegmentAtLevel(
  fullPath: string,
  prefixConsumed: string,
): { name: string; index?: number; remainder: string } | null {
  const consumesSameBranch = fullPath === prefixConsumed || fullPath.startsWith(`${prefixConsumed}.`);
  const remainder = prefixConsumed && consumesSameBranch
    ? fullPath.slice(prefixConsumed.length + 1)
    : fullPath;
  if (!remainder) return null;

  const nextSegment = remainder.split('.')[0];
  const match = nextSegment.match(/^([^[]+)(?:\[(\d+)])?$/);
  if (!match) return { name: remainder, remainder };

  return {
    name: match[1], index: match[2] !== undefined ? Number(match[2]) : undefined, remainder,
  };
}

const isDrillable = (field?: SchemaField): boolean => (
  field?.kind === 'object' || field?.kind === 'array'
);

function SchemaFieldLevel({
  workflowToken,
  internalName,
  pathPrefix,
  composedPrefix,
  onComposePath,
  initialValue,
  prefetchedRootFields,
}: {
  workflowToken: string;
  internalName: string;
  pathPrefix?: string;
  composedPrefix: string;
  onComposePath: (path: string) => void;
  initialValue?: string;
  // Root fields are already fetched by DestinationFieldPicker - reused instead of refetching.
  prefetchedRootFields?: SchemaField[];
}) {
  const [selectedField, setSelectedField] = useState<SchemaField | null>(null);
  const [arrayIndex, setArrayIndex] = useState(0);

  const { data } = useWorkflowVersionSchemaFields({
    workflowToken,
    internalName,
    pathPrefix,
    enabled: !prefetchedRootFields,
  });
  const fields = prefetchedRootFields ?? data?.fields ?? [];

  useEffect(() => {
    if (selectedField || !initialValue || fields.length === 0) return;

    const segment = getSegmentAtLevel(initialValue, composedPrefix);
    if (!segment) return;

    const matchedField = fields.find(field => field.name === segment.name);
    if (matchedField) {
      setSelectedField(matchedField);
      setArrayIndex(segment.index ?? 0);
      return;
    }

    setSelectedField({ name: segment.remainder, kind: 'primitive' });
    // Only run once fields become available, not on every subsequent user selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.length]);

  const composeAndEmit = (field: SchemaField, index: number) => {
    const segment = field.kind === 'array' ? `${field.name}[${index}]` : field.name;
    onComposePath(composedPrefix ? `${composedPrefix}.${segment}` : segment);
  };

  const onSelect = (selected: SelectedOption<SchemaField>) => {
    const option = selected as Option<SchemaField>;
    setSelectedField(option.meta);
    setArrayIndex(0);
    if (!isDrillable(option.meta)) {
      composeAndEmit(option.meta, 0);
    }
  };

  // Lets the user type any field name - the schema list is a convenience, not a restriction.
  const onCreateOption = (newOption: Option) => {
    const typedField: SchemaField = { name: newOption.value, kind: 'primitive' };
    setSelectedField(typedField);
    setArrayIndex(0);
    composeAndEmit(typedField, 0);
  };

  const onChangeIndex = (index: number) => {
    setArrayIndex(index);
    composeAndEmit(selectedField, index);
  };

  const joinSegment = (prefix: string, segment: string) => (prefix ? `${prefix}.${segment}` : segment);
  const nextPrefix = selectedField ? joinSegment(pathPrefix, selectedField.name) : null;
  const nextSegment = selectedField?.kind === 'array' ? `${selectedField.name}[${arrayIndex}]` : selectedField?.name;
  const nextComposedPrefix = selectedField ? joinSegment(composedPrefix, nextSegment) : null;

  return (
    <>
      <MultiSelect
        {...MULTISELECT_PROPS}
        openMenuOnFocus
        isCreatable
        onCreateOption={onCreateOption}
        options={fields.map(field => ({ label: field.name, value: field.name, meta: field }))}
        selectedOptions={selectedField
          ? { label: selectedField.name, value: selectedField.name }
          : null}
        placeholder="Payload Field"
        onChange={onSelect}
      />
      {selectedField?.kind === 'array' && (
        <TextField
          label="Index"
          type="number"
          value={String(arrayIndex)}
          onChange={e => onChangeIndex(Number(e.target.value) || 0)}
        />
      )}
      {isDrillable(selectedField) && (
        <SchemaFieldLevel
          // Keyed on name so switching fields remounts instead of reusing stale state.
          key={selectedField.name}
          workflowToken={workflowToken}
          internalName={internalName}
          pathPrefix={nextPrefix}
          composedPrefix={nextComposedPrefix}
          onComposePath={onComposePath}
          initialValue={initialValue}
        />
      )}
    </>
  );
}

// Cascading destination-field picker. customListFields skips fetching for Custom List targets;
// falls back to free text when there's no schema data either way.
export default function DestinationFieldPicker({
  internalName,
  value,
  onChange,
  customListFields,
}: {
  internalName: string;
  value: string;
  onChange: (value: string) => void;
  customListFields?: SchemaField[];
}) {
  const { workflowToken } = useParams();
  const isCustomList = customListFields !== undefined;
  const rootQuery = useWorkflowVersionSchemaFields({
    workflowToken, internalName, enabled: !isCustomList,
  });

  const fields = isCustomList ? customListFields : (rootQuery.data?.fields ?? []);
  if (fields.length === 0) {
    return (
      <TextField
        label="Payload Field"
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    );
  }

  return (
    <SchemaFieldLevel
      workflowToken={workflowToken}
      internalName={internalName}
      pathPrefix={undefined}
      composedPrefix=""
      onComposePath={onChange}
      initialValue={value}
      prefetchedRootFields={fields}
    />
  );
}
